import React, { Fragment, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumbs } from '../../../../AbstractElements';
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Form, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import { useCreateStoreMutation, useUpdateStoreMutation, useGetStoreByIdQuery } from '../../../../store/services/inventoryApi';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const storeSchema = yup.object().shape({
    storeName: yup.string().required('Store Name is required'),
    storeNumber: yup.string().nullable(),
    storeManager: yup.string().required('Store Manager is required'),
    phone: yup.string().required('Contact Number is required'),
    email: yup.string().email('Invalid email').required('Email is required'),
    password: yup.string().nullable(),
    storeAddress: yup.string().required('Store Address is required'),
    status: yup.string().oneOf(['active', 'inactive']).default('active'),
});

const StoreForm = () => {
    const { id } = useParams();
    const isEdit = !!id;
    const navigate = useNavigate();

    const [createStore, { isLoading: isCreating }] = useCreateStoreMutation();
    const [updateStore, { isLoading: isUpdating }] = useUpdateStoreMutation();
    const { data: storeResponse, isLoading: isLoadingData } = useGetStoreByIdQuery(id, { skip: !isEdit });

    const storeObject = React.useMemo(() => {
        if (!storeResponse) return null;
        return storeResponse.data || storeResponse;
    }, [storeResponse]);

    const { control, register, handleSubmit, reset, formState: { errors } } = useForm({
        resolver: yupResolver(storeSchema),
        context: { isEdit },
        defaultValues: {
            storeName: '',
            storeNumber: '',
            storeManager: '',
            phone: '',
            email: '',
            password: '',
            storeAddress: '',
            status: 'active'
        }
    });

    useEffect(() => {
        if (storeObject) {
            reset({
                storeName: storeObject.storeName || '',
                storeNumber: storeObject.storeNumber || '',
                storeManager: storeObject.storeManager || '',
                phone: storeObject.phone || '',
                email: storeObject.email || '',
                password: '',
                storeAddress: storeObject.storeAddress || '',
                status: storeObject.status || 'active',
            });
        }
    }, [storeObject, reset]);

    const onSubmit = async (data) => {
        const payload = {
            storeName: data.storeName,
            storeNumber: data.storeNumber,
            storeManager: data.storeManager,
            phone: data.phone,
            email: data.email,
            storeAddress: data.storeAddress,
            status: data.status,
        };
        if (!isEdit) {
            payload.password = data.password || 'gobooze@123';
        }

        try {
            if (isEdit) {
                await updateStore({ id, ...payload }).unwrap();
                toast.success('Store updated successfully');
            } else {
                await createStore(payload).unwrap();
                toast.success('Store created successfully');
            }
            navigate('/inventory/stores');
        } catch (err) {
            console.error("Store submit error:", err);
            toast.error(err?.data?.message || err?.error || 'Something went wrong');
        }
    };

    if (isEdit && isLoadingData) return <div className="p-5 text-center">Loading...</div>;

    return (
        <Fragment>
            <Breadcrumbs parent="Inventory" title={isEdit ? "Edit Store" : "Add Store"} mainTitle="Store Form" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader>
                                <h5>{isEdit ? "Edit Store Details" : "New Store Details"}</h5>
                            </CardHeader>
                            <CardBody>
                                <Form onSubmit={handleSubmit(onSubmit)}>
                                    <h6 className="pb-2 mb-3 border-bottom">Basic Information</h6>
                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label>Store Name <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="storeName"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input {...field} type="text" invalid={!!errors.storeName} />
                                                    )}
                                                />
                                                <FormFeedback>{errors.storeName?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label>Store Number</Label>
                                                <Controller
                                                    name="storeNumber"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input {...field} type="text" invalid={!!errors.storeNumber} />
                                                    )}
                                                />
                                                <FormFeedback>{errors.storeNumber?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <h6 className="pb-2 mb-3 border-bottom mt-3">Manager & Contact Details</h6>
                                    <Row>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label>Store Manager <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="storeManager"
                                                    control={control}
                                                    render={({ field }) => <Input {...field} type="text" invalid={!!errors.storeManager} />}
                                                />
                                                <FormFeedback>{errors.storeManager?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label>Email <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="email"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input {...field} type="email" invalid={!!errors.email} />
                                                    )}
                                                />
                                                <FormFeedback>{errors.email?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label>Contact Number <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="phone"
                                                    control={control}
                                                    render={({ field }) => <Input {...field} type="text" invalid={!!errors.phone} />}
                                                />
                                                <FormFeedback>{errors.phone?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        {!isEdit && (
                                            <Col md="6">
                                                <FormGroup>
                                                    <Label>Password (Default: gobooze@123)</Label>
                                                    <Controller
                                                        name="password"
                                                        control={control}
                                                        render={({ field }) => <Input {...field} type="password" invalid={!!errors.password} placeholder="Optional" />}
                                                    />
                                                    <FormFeedback>{errors.password?.message}</FormFeedback>
                                                </FormGroup>
                                            </Col>
                                        )}
                                        <Col md={isEdit ? "12" : "6"}>
                                            <FormGroup>
                                                <Label>Status</Label>
                                                <Controller
                                                    name="status"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input {...field} type="select" invalid={!!errors.status}>
                                                            <option value="active">Active</option>
                                                            <option value="inactive">Inactive</option>
                                                        </Input>
                                                    )}
                                                />
                                                <FormFeedback>{errors.status?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <h6 className="pb-2 mb-3 border-bottom mt-3">Address Information</h6>
                                    <Row>
                                        <Col md="12">
                                            <FormGroup>
                                                <Label>Store Address <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="storeAddress"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input {...field} type="textarea" rows={3} invalid={!!errors.storeAddress} placeholder="Enter full address..." />
                                                    )}
                                                />
                                                <FormFeedback>{errors.storeAddress?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                     <div className="mt-4 d-flex justify-content-end">
                                         <Button color="primary" outline className="me-2" onClick={() => navigate('/inventory/stores')}>Cancel</Button>
                                         <Button color="primary" type="submit" disabled={isCreating || isUpdating}>
                                            {isCreating || isUpdating ? 'Saving...' : (isEdit ? 'Update Store' : 'Create Store')}
                                        </Button>
                                    </div>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default StoreForm;
